import { Link, useParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/common/Button';
import { Icon } from '../components/common/Icon';
import { Reveal } from '../components/animation/Reveal';
import { LoadingScreen } from '../components/common/Spinner';
import { ProductCard } from '../components/product/ProductCard';
import { breadcrumbSchema, collectionPageSchema, itemListSchema } from '../lib/seo';
import { articlesForCategory } from '../data/knowledge';
import { cropSolutionFor } from '../lib/cropLinks';
import { useCategory, useCategories, useProductsByCategory } from '../hooks';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const { data: category, isLoading } = useCategory(slug);
  const { data: products = [] } = useProductsByCategory(slug);
  const { data: categories = [] } = useCategories();

  if (isLoading) {
    return (
      <div className="pt-[var(--nav-h)]">
        <LoadingScreen label="Loading category" />
      </div>
    );
  }

  if (!category) {
    return (
      <>
        <PageHeader title="Category not found" subtitle="This product category doesn’t exist." />
        <div className="container-x py-16">
          <Button to="/catalog" variant="primary" size="lg">
            <Icon name="ArrowLeft" className="h-5 w-5" /> All products
          </Button>
        </div>
      </>
    );
  }

  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/catalog' },
    { name: category.name, path: `/product-categories/${category.slug}` },
  ];

  // Crops this category is documented for = the union of its products' crop tags that have a
  // programme page. Derived from the catalogue, so it cannot drift from the products themselves.
  const crops = [
    ...new Map(
      products
        .flatMap((p) => p.recommendedCrops ?? [])
        .map((tag) => cropSolutionFor(tag))
        .filter((c): c is NonNullable<typeof c> => Boolean(c))
        .map((c) => [c.slug, c]),
    ).values(),
  ];
  const articles = articlesForCategory(category.slug);

  return (
    <>
      <Seo
        title={`${category.name} — ${category.tagline} | GreenbayBio`}
        description={category.description}
        path={`/product-categories/${category.slug}`}
        jsonLd={[
          collectionPageSchema({
            name: `${category.name} — GreenbayBio`,
            description: category.description,
            path: `/product-categories/${category.slug}`,
          }),
          itemListSchema(
            `${category.name} products`,
            `/product-categories/${category.slug}`,
            products.map((p) => ({ name: p.name, path: `/catalog/${p.slug}` })),
          ),
          breadcrumbSchema(crumbs),
        ]}
      />
      <PageHeader eyebrow={category.tagline} title={category.name} subtitle={category.description} accent={category.accentColor} />

      <div className="container-x py-14">
        {/* Direct answer: what this category is and what is in it, before anything else. */}
        <p className="max-w-3xl text-xl leading-relaxed text-ink">
          {category.description}{' '}
          {products.length > 0
            ? `GreenbayBio's ${category.name.toLowerCase()} range holds ${products.length} product${products.length === 1 ? '' : 's'}: ${products.map((p) => p.name).join(', ')}.`
            : ''}
        </p>
        <p className="mb-8 mt-6 text-sm text-ink-500">
          <span className="font-semibold text-ink">{products.length}</span> {products.length === 1 ? 'product' : 'products'} in this category
        </p>
        {products.length > 0 ? (
          <Reveal className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" stagger={0.1} y={26}>
            {products.map((p, i) => (
              <ProductCard key={p.slug} product={p} priority={i < 3} />
            ))}
          </Reveal>
        ) : (
          <p className="rounded-xl border border-dashed border-sand-200 bg-white px-6 py-14 text-center text-ink-500">
            No products are assigned to this category yet.
          </p>
        )}
      </div>

      {(crops.length > 0 || articles.length > 0) && (
        <div className="border-t border-sand-200">
          <div className="container-x grid gap-12 py-12 md:grid-cols-2">
            {crops.length > 0 && (
              <section aria-labelledby="cat-crops-heading">
                <h2 id="cat-crops-heading" className="font-display text-h3 font-semibold text-ink">
                  Crops these products are documented for
                </h2>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {crops.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/crop-solutions/${c.slug}`}
                      className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 transition-colors hover:border-brand-400 hover:text-brand-700"
                    >
                      <Icon name="Sprout" className="h-4 w-4 text-brand-500" />
                      {c.name}
                    </Link>
                  ))}
                </div>
                <p className="mt-3 text-sm text-ink-500">
                  Each programme lists the stage and dose stated in the product specifications.
                </p>
              </section>
            )}

            {articles.length > 0 && (
              <section aria-labelledby="cat-science-heading">
                <h2 id="cat-science-heading" className="font-display text-h3 font-semibold text-ink">
                  The science behind this category
                </h2>
                <ul className="mt-4 space-y-3">
                  {articles.map((a) => (
                    <li key={a.slug}>
                      <Link to={`/knowledge/${a.slug}`} className="group block">
                        <span className="font-semibold text-ink group-hover:text-brand-700">{a.title}</span>
                        <span className="mt-1 block text-sm leading-relaxed text-ink-500">{a.summary}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      )}

      <div className="border-t border-sand-200 bg-sand-100/50">
        <div className="container-x py-12">
          <h2 className="mb-6 font-display text-h3 font-semibold text-ink">Other categories</h2>
          <div className="flex flex-wrap gap-3">
            {categories
              .filter((c) => c.slug !== category.slug)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/product-categories/${c.slug}`}
                  className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-700 transition-colors hover:border-brand-400 hover:text-brand-600"
                >
                  <span className="h-2 w-2 rounded-full" style={{ background: c.accentColor }} />
                  {c.name}
                </Link>
              ))}
          </div>
        </div>
      </div>
    </>
  );
}
